import React from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ArrowLeft, LayoutList } from "lucide-react";
import EmailCreativeVersionTable from "./EmailCreativeVersionTable";

/**
 * EmailCreativeDetail — DASH-EMAIL-011b, cut over in DASH-EMAIL-012A.
 *
 * Creative identity/metadata plus the full Version history, from the real
 * CreativeDetailResponse. Creative is version-spanning — the "N Versions"
 * badge here is the SAME always-visible disclosure EmailCreativeCatalogTable
 * already renders on the list row, carried through into detail rather than
 * a single flattened Fingerprint standing in for the whole Creative.
 *
 * `firstUsed`/`lastUsed` are gone (DASH-EMAIL-012A Frozen Decision 12): no
 * real execution/use timestamp exists anywhere in the governed catalog —
 * removed rather than derived from any created/updated timestamp.
 */
export default function EmailCreativeDetail({
  detail,
  angleLookup,
  campaignLookup,
  initialExpandedVersion,
  onBack,
  onNavigateToAsset,
}) {
  if (!detail) return null;

  const angleName = detail.angle_id ? angleLookup?.[detail.angle_id] || detail.angle_id : "—";
  const campaignName = campaignLookup?.[detail.campaign_id] || detail.campaign_id;

  return (
    <div className="space-y-4" data-testid="email-creative-detail">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1.5 text-slate-300 hover:text-slate-100"
        onClick={onBack}
        data-testid="email-creative-detail-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Creatives
      </Button>

      <Card className="glass border-white/10" data-testid="email-creative-detail-identity">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <LayoutList className="h-5 w-5 text-indigo-400" />
            <span className="text-lg font-semibold text-slate-100">{detail.name}</span>
            <Badge className="bg-indigo-500/20 text-indigo-300 text-xs" data-testid="email-creative-detail-version-badge">
              {detail.version_count} {detail.version_count === 1 ? "version" : "versions"}
            </Badge>
            <span className="text-sm text-slate-400">{detail.status}</span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-300 sm:grid-cols-4">
            <span>
              Creative ID: <span className="text-slate-100">{detail.creative_id}</span>
            </span>
            <span>
              Program: <span className="text-slate-100">{detail.program_id || "—"}</span>
            </span>
            <span>
              Campaign: <span className="text-slate-100">{campaignName}</span>
            </span>
            <span>
              Primary Angle: <span className="text-slate-100">{angleName}</span>
            </span>
            <span>
              Current Version: <span className="text-slate-100">{detail.current_version || "—"}</span>
            </span>
            <span>
              Broadcasts: <span className="text-slate-100">{detail.broadcast_count}</span>
            </span>
          </div>
        </CardContent>
      </Card>

      <EmailCreativeVersionTable
        // Keyed on the exact navigation target (React's "reset state via
        // remount" pattern) so a fresh initialExpandedVersion — even for
        // the SAME Creative already open — correctly re-initializes which
        // Version row starts expanded, without a setState-in-effect. See
        // EmailCreativeVersionTable's own header comment.
        key={`${detail.creative_id}-${initialExpandedVersion || "none"}`}
        creativeId={detail.creative_id}
        versions={detail.versions}
        angleLookup={angleLookup}
        campaignLookup={campaignLookup}
        initialExpandedVersion={initialExpandedVersion}
        onNavigateToAsset={onNavigateToAsset}
      />
    </div>
  );
}
